import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiPost } from './apiClient';
import { fetchEndProduct } from './communityApi';
import { trackEvent } from './analytics';

// Phase Hub-Detail (2026-05-23) — simplified Product Feedback page.
// Removed: existing-issue search box + search-result list + Type/Severity
// dropdowns (the back-end triage queue defaults them sensibly and the
// noise wasn't producing better reports). Added: prompt + model display
// for prompt-category products so the user has the context they need to
// describe what went wrong with the generation.

export default function FeedbackPage() {
  const [searchParams] = useSearchParams();

  const productContext = useMemo(() => {
    const dna = searchParams.get('dna');
    const endProductID = searchParams.get('productId');
    const endProductSlug = searchParams.get('slug');
    if (!dna && !endProductID && !endProductSlug) return null;
    return {
      dna_uuid: dna || null,
      endProductID: endProductID || null,
      endProductSlug: endProductSlug || null,
      version: searchParams.get('v') || '1.0.0',
      source: searchParams.get('src') || 'unknown',
      name: searchParams.get('name') || null,
      previewURL: searchParams.get('preview') || null,
    };
  }, [searchParams]);

  // Full product doc so we can surface prompt + model on the card. The URL
  // params don't carry the prompt body (would be too long), so we re-fetch
  // by id/slug. Best-effort: nil if offline or doc gone.
  const [productDoc, setProductDoc] = useState(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (!productContext) return;
    const idOrSlug = productContext.endProductID || productContext.endProductSlug;
    if (!idOrSlug) return;
    let cancelled = false;
    fetchEndProduct(idOrSlug).then((p) => {
      if (!cancelled && p) setProductDoc(p);
    }).catch(() => { /* silent — surface area is feedback, not detail */ });
    return () => { cancelled = true; };
  }, [productContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title,
        description,
        type: 'bug',
        severity: 'medium',
        rating: rating || undefined,
      };
      if (productContext) {
        payload.dna_uuid = productContext.dna_uuid;
        payload.version = productContext.version;
        payload.endProductID = productContext.endProductID;
        payload.endProductSlug = productContext.endProductSlug;
        payload.endProductName = productContext.name;
        payload.previewURL = productContext.previewURL;
        payload.metadata = { generator_source: productContext.source, source: productContext.source };
      }
      const res = await apiPost('/v1/feedback', payload);
      const id = (res.ticketId || res.issueId || '').slice(0, 8);
      trackEvent('feedback_submitted', {
        rating: rating || 0,
        source: productContext ? 'product' : 'general',
      });
      setSuccessMsg(id ? `Feedback submitted! Review ID: ${id}…` : 'Feedback submitted!');
      setDescription('');
      setTitle('');
      setRating(0);
    } catch (err) {
      console.error("Submit error:", err);
      alert('Failed to submit feedback: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const starColor = (i) => (i <= (hoverRating || rating)) ? '#f59e0b' : '#d1d5db';

  const isPrompt = (productDoc?.contentCategory || '').toLowerCase() === 'prompt';
  const promptBody = productDoc?.prompt || '';
  const promptModel = productDoc?.modelHint || '';

  return (
    <div style={{ background: 'var(--bg-primary, #fff)', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px' }}>

        {/* Back link */}
        <Link to="/cloud" style={{ color: 'var(--accent-primary, #3b82f6)', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 24 }}>
          <span style={{ fontSize: '1.1em' }}>&larr;</span> Back to Cloud
        </Link>

        {/* Header */}
        <h1 style={{ color: 'var(--text-primary, #111827)', margin: '0 0 8px', fontSize: '1.75rem', fontWeight: 800 }}>Product Feedback</h1>
        <p style={{ color: 'var(--text-secondary, #6b7280)', margin: '0 0 32px', fontSize: '1rem', lineHeight: 1.5 }}>
          Rate your experience and report any issues you ran into.
        </p>

        {/* Product context card */}
        {productContext && (
          <div style={{ background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-alt, #e5e7eb)', borderRadius: 12, padding: '20px 24px', marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.4rem', fontWeight: 700, flexShrink: 0 }}>
                {productContext.name ? productContext.name.charAt(0) : '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--text-primary, #111827)', fontWeight: 700, fontSize: '1.1rem' }}>
                  {productContext.name || 'Generated Product'}
                </div>
                <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.85rem', marginTop: 2 }}>
                  v{productContext.version} &middot; {productContext.source}
                </div>
              </div>
            </div>

            {/* Prompt + model — only present for prompt-category products. */}
            {isPrompt && promptBody && (
              <div style={{ marginTop: 4, marginBottom: 4 }}>
                <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>
                  Prompt
                </div>
                <div style={{
                  background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-alt, #e5e7eb)',
                  borderRadius: 8, padding: '12px 14px',
                  color: 'var(--text-primary, #111827)', fontSize: '0.9rem', lineHeight: 1.5,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {promptBody}
                </div>
                {promptModel && (
                  <div style={{ marginTop: 8, color: 'var(--text-secondary, #6b7280)', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: '0.85rem' }}>🧠</span>
                    <span><strong style={{ color: 'var(--text-primary, #111827)', fontWeight: 600 }}>Model:</strong> {promptModel}</span>
                  </div>
                )}
              </div>
            )}

            {/* Star Rating */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-alt, #e5e7eb)' }}>
              <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.85rem', fontWeight: 500, marginBottom: 8 }}>Overall Rating</div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    onMouseEnter={() => setHoverRating(i)}
                    onMouseLeave={() => setHoverRating(0)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: '1.6rem', color: starColor(i), transition: 'color 0.1s, transform 0.1s', transform: i <= (hoverRating || rating) ? 'scale(1.15)' : 'scale(1)' }}
                    aria-label={`Rate ${i} star${i > 1 ? 's' : ''}`}
                  >
                    &#9733;
                  </button>
                ))}
                {rating > 0 && (
                  <span style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.85rem', marginLeft: 8 }}>
                    {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                  </span>
                )}
              </div>
            </div>

            {/* Link back to product */}
            {(productContext.endProductSlug || productContext.previewURL) && (
              <div style={{ marginTop: 12 }}>
                <a
                  href={productContext.endProductSlug ? `/hub/${productContext.endProductSlug}` : productContext.previewURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent-primary, #3b82f6)', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'none' }}
                >
                  {productContext.endProductSlug ? 'Open product page →' : 'Open preview →'}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Report a Bug — simplified to Title + Description. Type defaults to
            'bug' and severity to 'medium' server-side; raising/lowering
            severity is a triage-team job, not the reporter's. */}
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ color: 'var(--text-primary, #111827)', margin: '0 0 20px', fontSize: '1.1rem', fontWeight: 700 }}>
            Report a Bug
          </h3>

          {successMsg ? (
            <div style={{ color: '#166534', padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', fontWeight: 500 }}>
              {successMsg}
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={{ display: 'block', color: 'var(--text-primary, #111827)', marginBottom: 6, fontWeight: 600, fontSize: '0.85rem' }}>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of the issue"
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #111827)', border: '1px solid var(--border-alt, #e5e7eb)', fontSize: '0.95rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: 'var(--text-primary, #111827)', marginBottom: 6, fontWeight: 600, fontSize: '0.85rem' }}>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={"What happened?\n\nWhat were you trying to do?\n\nAnything else we should know?"}
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: 8, background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #111827)', border: '1px solid var(--border-alt, #e5e7eb)', minHeight: 140, fontSize: '0.95rem', fontFamily: "'Inter', sans-serif", lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{ background: 'var(--accent-primary, #3b82f6)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.95rem', opacity: submitting ? 0.6 : 1, transition: 'opacity 0.15s', alignSelf: 'flex-start' }}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
